/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import {Formik} from 'formik';
import moment from 'moment';

import ConfirmModal from 'components/ConfirmModal';
import api from 'utils/api';
import {gettext} from 'utils/text';
import {orderType} from 'propTypes/orders';
import {ORDERS} from 'constants/sales';


// Session storage key for keeping track of checked orders on multiple pages
const CHECKED_ORDERS = 'checkedOrders';


function parseJSONSafely(str) {
    try {
        return JSON.parse(str);
    } catch (e) {
        return {};
    }
}

const WB = "local";
const CMR = "cmr";


class OrderList extends React.Component {

    static propTypes = {
        orders: PropTypes.arrayOf(orderType.isRequired).isRequired,
        ordering: PropTypes.shape({
            number: PropTypes.string.isRequired,
            clientName: PropTypes.string.isRequired,
            customerNumber: PropTypes.string.isRequired,
            articleCode: PropTypes.string.isRequired,
            deliveryDate: PropTypes.string.isRequired,
            QCRequired: PropTypes.string.isRequired,
        }).isRequired,
        nextQueryParam: PropTypes.string.isRequired,
    };

    state = {
        sessionSelectedOrders: {},
        isGenerating: false,
        isExporting: false,
        confirmModal: null,
        error: null,
    };

    componentDidMount() {
        this.updateSessionSelectedOrders();
    }

    getSelectedCount = (filterMethod) => {
        const maybeItem = window.sessionStorage.getItem(CHECKED_ORDERS);
        let checkedOrders;
        if (maybeItem) {
            checkedOrders = parseJSONSafely(maybeItem);
            return Object.values(checkedOrders).filter(filterMethod === undefined ? () => true : filterMethod).length;
        }
        return 0;
    }

    hideModal = () => {
        this.setState({confirmModal: null});
    };

    updateSessionSelectedOrders = () => {
        const sessionOrders = window.sessionStorage.getItem(CHECKED_ORDERS) || {};
        this.setState({sessionSelectedOrders: parseJSONSafely(sessionOrders)});
    };

    handleExportOrdersClick = () => {
        this.setState({isExporting: true});
    };

    exportUrlAndWaitResponse = () => {
        if (this.props.orders.length > 0) {
            // Add export parameter to url in order to trigger export to excel method in OrderListView
            const parsedUrl = new URL(window.location.href);
            parsedUrl.searchParams.append('export', 'true');
            if (this.state.isExporting) {
                fetch(parsedUrl.href)
                    .then((response) => {
                        if (!response.ok) {
                            this.setState({isExporting: false});
                            throw Error(response.statusText);
                        }
                    })
                    .then(() => {
                        this.setState({isExporting: false});
                    })
                    .catch(error => console.error(error));
            }
            return parsedUrl.href;
        }
        return null;
    };

    storeToSession = (isChecked, order, updateState = true) => {
        /**
         * Store checked orders in the session store under the key CHECKED_ORDERS.
         * The value for that key is an object, where keys are object ids and
         * values are data for the object.
         */
        const maybeItem = window.sessionStorage.getItem(CHECKED_ORDERS);
        const orderData = {id: order.id,
            full_number: order.full_number,
            status: order.status,
            local_waybill_path: order.local_waybill_path,
            cmr_waybill_path: order.cmr_waybill_path};
        let checkedOrders;
        if (maybeItem) {
            checkedOrders = parseJSONSafely(maybeItem);
            if (isChecked) {
                checkedOrders[order.id] = orderData;
            } else {
                delete checkedOrders[order.id];
            }
        } else if (isChecked) {
            checkedOrders = {[order.id]: orderData};
        }
        if (checkedOrders) {
            window.sessionStorage.setItem(CHECKED_ORDERS, JSON.stringify(checkedOrders));
        } else {
            window.sessionStorage.removeItem(CHECKED_ORDERS);
        }
        if (updateState) this.updateSessionSelectedOrders();
    };


    sendGenerateWaybillRequest = (idsOfCheckedOrders, orders, setFieldValue, type) => {
        this.setState({isGenerating: type});
        api.salesOrdersGenerateDocument.post(null, {checkedOrders: idsOfCheckedOrders, type}).then(
            (data) => {
                const localLinks = data.links.local || {};
                const cmrLinks = data.links.cmr || {};

                for (let i = 0; i < orders.length; i++) {
                    if (orders[i].id in localLinks) {
                        setFieldValue(`orders[${i}].local_waybill_path`, localLinks[orders[i].id]);
                    }
                    if (orders[i].id in cmrLinks) {
                        setFieldValue(`orders[${i}].cmr_waybill_path`, cmrLinks[orders[i].id]);
                    }
                }
            },
            (err) => {
                if (err.isValidationError) {
                    const error = parseJSONSafely(err.responseText)[0];
                    this.setState({error});
                } else {
                    console.error(err);
                }
            },
        ).finally(() => {
            window.sessionStorage.removeItem(CHECKED_ORDERS);
            this.updateSessionSelectedOrders();
            this.setState({isGenerating: false});
            for (let i = 0; i < orders.length; i++) {
                setFieldValue(`orders[${i}].is_loading`, false);
                setFieldValue(`orders[${i}].is_checked`, false);
            }
        });
    };

    handleGenerateDocumentButtonPressed = (orders, setFieldValue, type) => {
        const warningMessage = gettext("Order number %s does not have status ready!");
        const sessionSelectedOrders = this.state.sessionSelectedOrders;

        const warnings = [];
        const selectedOrders = [];
        Object.keys(sessionSelectedOrders).forEach((k) => {
            const order = sessionSelectedOrders[k];
            selectedOrders.push(order);
            if (order.status !== ORDERS.STATUS.READY) {
                const orderNumber = order.full_number;
                warnings.push({msg: interpolate(warningMessage, [orderNumber]), key: orderNumber});
            }
        });

        this.setState({
            confirmModal: {
                warnings,
                selectedOrders,
                description: gettext('Are you sure you want to generate documents for the selected orders? ' +
                    'This can only be done once for every order.'),
                onConfirm: () => {
                    for (let i = 0; i < orders.length; i++) {
                        if (orders[i].is_checked) {
                            setFieldValue(`orders[${i}].is_loading`, true);
                        }
                    }
                    this.sendGenerateWaybillRequest(
                        Object.keys(sessionSelectedOrders).map(Number), orders, setFieldValue, type,
                    );
                },
            },
        });
    };

    renderFormLayout = (layoutProps) => {
        const {handleChange, values, setFieldValue} = layoutProps;

        const orderListLink = DJ_CONST.reverse['sales:order-list']();


        // gettext does not find them when they are inside React list
        const wbDisplay = gettext("WB");
        const cmrDisplay = gettext("CMR");
        const hasNoWb = order => order.local_waybill_path === null;
        const hasNoCmr = order => order.cmr_waybill_path === null;
        const hasWb = order => order.local_waybill_path;
        const hasCmr = order => order.cmr_waybill_path;
        const {isGenerating} = this.state;

        const generateDocumentBlock = (
            <span>
                <ConfirmModal modal={this.state.confirmModal} onHide={this.hideModal} />
                <div className="d-flex justify-content-start align-items-center mb-3">
                    <h1>{gettext("Sales orders")}</h1>
                    <div className="ml-auto">
                        <button
                            className="btn btn-warning bs-warning text-uppercase font-weight-bold fs-sm mr-2"
                            name="generate-wb"
                            disabled={
                                isGenerating === WB ||
                                !this.getSelectedCount(hasNoWb) || this.getSelectedCount(hasWb)
                            }
                            onClick={
                                () => this.handleGenerateDocumentButtonPressed(values.orders, setFieldValue, WB)
                            }
                        >
                            {gettext("Generate Waybill")}
                        </button>
                        <button
                            className="btn btn-warning bs-warning text-uppercase font-weight-bold fs-sm"
                            name="generate-cmr"
                            disabled={
                                isGenerating === CMR ||
                                !this.getSelectedCount(hasNoCmr) || this.getSelectedCount(hasCmr)
                            }
                            onClick={
                                () => this.handleGenerateDocumentButtonPressed(values.orders, setFieldValue, CMR)
                            }
                        >
                            {gettext("Generate CMR")}
                        </button>
                    </div>
                </div>
            </span>
        );

        const toggleAll = (filterMethod, checkOrUncheckOverride) => {
            const orders = values.orders.filter(filterMethod);
            // only uncheck when all boxes are checked
            const checkOrUncheck = checkOrUncheckOverride === undefined ?
                orders.filter(o => !o.is_checked).length !== 0 : checkOrUncheckOverride;
            orders.forEach((order) => {
                setFieldValue(`orders[${values.orders.indexOf(order)}].is_checked`, checkOrUncheck);
                this.storeToSession(checkOrUncheck, order, false);
            });
            this.updateSessionSelectedOrders();
        };

        return (
            <div>
                {/* external users should not be able to create documents */}
                {!DJ_CONST.user.external && generateDocumentBlock}
                <div className="p-1 mb-3">
                    <a
                        className="btn btn-info text-uppercase font-weight-bold fs-sm mr-2"
                        href={this.exportUrlAndWaitResponse()}
                        onClick={this.handleExportOrdersClick}
                        download
                    >
                        {!this.state.isExporting
                            ? gettext("Export Orders")
                            : <FontAwesomeIcon icon="spinner" pulse size="1x" />
                        }
                    </a>
                    {!DJ_CONST.user.external && <button
                        disabled={!this.getSelectedCount() || isGenerating}
                        className={"btn btn-warning text-uppercase font-weight-bold fs-sm mr-2"} onClick={() => {
                            window.sessionStorage.removeItem(CHECKED_ORDERS);
                            toggleAll(() => true, false);
                        }}
                    >
                        {gettext("Deselect")} {this.getSelectedCount()}
                    </button>}
                </div>
                <div className="table-responsive">
                    <table className="table table-sm table-striped table-hover">
                        <thead>
                            <tr>


                                {!DJ_CONST.user.external && ([
                                    <th key="chk-th" scope="col" />,
                                    <th key="wb-th" scope="col">
                                        <button
                                            className={"btn btn-warning btn-sm text-uppercase fs-xs m-0"}
                                            disabled={isGenerating || values.orders.filter(hasNoWb).length === 0}
                                            style={{cursor: "pointer"}}
                                            onClick={
                                                 () => toggleAll(hasNoWb)}
                                        >
                                            {isGenerating !== WB ? wbDisplay :
                                            <FontAwesomeIcon icon="spinner" pulse size="1x" />}
                                        </button></th>,
                                    <th key="cmr-th" scope="col">
                                        <button
                                            disabled={isGenerating || values.orders.filter(hasNoCmr).length === 0}
                                            className={"btn btn-warning btn-sm text-uppercase fs-xs m-0"}
                                            onClick={() => toggleAll(hasNoCmr)}
                                        >
                                            {isGenerating !== CMR ? cmrDisplay :
                                            <FontAwesomeIcon icon="spinner" pulse size="1x" />}
                                        </button></th>,
                                ])}
                                <th scope="col" colSpan="2">
                                    <a href={`${orderListLink}?${values.ordering.number}`}>
                                        {gettext("Order number")}
                                    </a>
                                </th>
                                <th scope="col">
                                    <a href={`${orderListLink}?${values.ordering.clientName}`}>
                                        {gettext("Client")}
                                    </a>
                                </th>
                                <th scope="col">
                                    <a href={`${orderListLink}?${values.ordering.customerNumber}`}>
                                        {gettext("Client Order Number")}
                                    </a>
                                </th>
                                <th scope="col">
                                    <a href={`${orderListLink}?${values.ordering.articleCode}`}>
                                        {gettext("Product")}
                                    </a>
                                </th>
                                <th scope="col">
                                    {gettext("Information")}
                                </th>
                                <th scope="col">
                                    <a href={`${orderListLink}?${values.ordering.deliveryDate}`}>
                                        {gettext("Estimated delivery date")}
                                    </a>
                                </th>
                                {!DJ_CONST.user.external && (
                                    <th scope="col">
                                        <a href={`${orderListLink}?${values.ordering.QCRequired}`}>
                                            {gettext("QA")}
                                        </a>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {values.orders.length
                                ? values.orders.map((order, index) => (
                                    <tr key={order.id}>
                                        {!DJ_CONST.user.external && [
                                            <td key="chk-td" className="pl-5">
                                                {
                                                    !order.is_loading &&
                                                    (
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            name={`orders[${index}].is_checked`}
                                                            onChange={
                                                                (e) => {
                                                                    handleChange(e);
                                                                    this.storeToSession(e.target.checked, order);
                                                                }
                                                            }
                                                            checked={order.is_checked}
                                                            disabled={
                                                                order.local_waybill_path && order.cmr_waybill_path
                                                            }
                                                        />
                                                    )
                                                }
                                            </td>,
                                            <td key="wb-td">
                                                {(() => {
                                                    if (order.is_loading && isGenerating === WB) {
                                                        return (<FontAwesomeIcon icon="spinner" pulse size="1x" />);
                                                    } else if (order.local_waybill_path) {
                                                        return (
                                                            <a
                                                                href={order.local_waybill_path}
                                                                className="badge badge-info"
                                                                download
                                                            >
                                                                {gettext("WB")}
                                                            </a>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </td>,
                                            <td key="cmr-td">
                                                {(() => {
                                                    if (order.is_loading && isGenerating === CMR) {
                                                        return (<FontAwesomeIcon icon="spinner" pulse size="1x" />);
                                                    } else if (order.cmr_waybill_path) {
                                                        return (
                                                            <a
                                                                href={order.cmr_waybill_path}
                                                                className="badge badge-info"
                                                                download
                                                            >
                                                                {gettext("CMR")}
                                                            </a>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </td>,
                                        ]}
                                        <th scope="row" className="text-nowrap">
                                            {!DJ_CONST.user.external ? [
                                                <a
                                                    key="order-number"
                                                    href={`${order.absolute_url}?${values.nextQueryParam}`}
                                                    className="text-dark"
                                                >
                                                    {order.full_number}
                                                </a>,
                                                <br key="br" />,
                                                <span key="order-date" className="text-muted font-weight-light">
                                                    {order.order_date}
                                                </span>,
                                            ]
                                                : (
                                                    <div>
                                                        {order.full_number}
                                                        <br />
                                                        <span className="text-muted font-weight-light">
                                                            {order.order_date}
                                                        </span>
                                                    </div>
                                                )}
                                        </th>
                                        <td className="text-center text-nowrap">{order.status_display}</td>
                                        <td className="text-nowrap">{order.client_name}</td>
                                        <td className="text-nowrap">{order.customer_number}</td>
                                        <td>
                                            {order.article_rows.map(item => (
                                                <div className="mb-2" key={item.articleCode}>
                                                    <span className="text-nowrap">{item.articleCode}</span>
                                                    <br />
                                                    <span className="text-muted">{item.articleName}</span>
                                                </div>
                                            ))}
                                        </td>
                                        <td>
                                            <dl className="row">
                                                {
                                                    /*
                                                    Note: If we upgraded React to >16.2.0, we could use
                                                    reactjs.org/blog/2017/11/28/react-v16.2.0-fragment-support.html
                                                    instead of arrays. Babel needs to be >7.0.0 as well to support this.
                                                    */
                                                }
                                                {order.info && [
                                                    <dt key="dt-in" className="col-xl-3">{gettext("Info")}</dt>,
                                                    <dd key="dd-in" className="col-xl-9">{order.info}</dd>,
                                                ]}
                                                {order.width && [
                                                    <dt key="dt-wi" className="col-xl-3">{gettext("Width")}</dt>,
                                                    <dd key="dd-wi" className="col-xl-9">{order.width}</dd>,
                                                ]}
                                                {order.fastening && [
                                                    <dt key="dt-fa" className="col-xl-3">{gettext("Fastening")}</dt>,
                                                    <dd key="dd-fa" className="col-xl-9">{order.fastening}</dd>,
                                                ]}
                                                {order.extras && [
                                                    <dt key="dt-ex" className="col-xl-3">{gettext("Extras")}</dt>,
                                                    <dd key="dd-ex" className="col-xl-9">{order.extras}</dd>,
                                                ]}
                                                {order.transport && [
                                                    <dt key="dt-tr" className="col-xl-3">{gettext("Transport")}</dt>,
                                                    <dd key="dd-tr" className="col-xl-9">{order.transport}</dd>,
                                                ]}
                                            </dl>
                                        </td>
                                        <td className="text-nowrap">
                                            {order.delivery_date && moment(order.delivery_date).format("DD.MM.YYYY")}
                                        </td>
                                        {!DJ_CONST.user.external && (
                                            <td className="text-nowrap">
                                                {order.is_quality_check_required ? gettext("Yes") : gettext("No")}
                                            </td>
                                        )}
                                    </tr>
                                ))
                                : (
                                    <tr>
                                        <td
                                            colSpan="6"
                                            className="text-center lead py-5 bg-light"
                                        >
                                            {gettext("No sales orders found")}
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    render() {
        const initialValues = this.props;
        const checkedOrders = window.sessionStorage.getItem(CHECKED_ORDERS);

        if (checkedOrders && checkedOrders != null) {
            const checked = parseJSONSafely(checkedOrders);
            for (let i = 0; i < initialValues.orders.length; i++) {
                const order = initialValues.orders[i];
                if (order.id in checked) {
                    order.is_checked = true;
                }
            }
        }

        return (
            <div>
                {this.state.error &&
                <div className="alert alert-danger">
                    {this.state.error}
                </div>
                }
                <Formik
                    initialValues={initialValues}
                    render={this.renderFormLayout}
                />
            </div>
        );
    }
}


export default OrderList;



// WEBPACK FOOTER //
// ./src/components/OrderList.js